<script setup lang="ts">
import type { McpServer, McpTool } from 'shared/src'
import { isNotBlank, toSafeSnakeCase } from '@/utils/String'
import fetchApi from '@/utils/fetchApi'
import {
  mergeDiscoveredTools,
  type McpDiscoveredTool,
  type McpTestResult,
} from '@/composables/configurator/useMcpServers'
import { useFeatureFlags } from '@/composables/env/useFeatureFlags'
const { showMcpConfigOverride } = useFeatureFlags()

const { t } = useI18n()

type Status = 'untested' | 'connected' | 'error'

const panelOpen = ref(false)
const isEditing = ref(false)
const status = ref<Status>('untested')

interface InputRef {
  $el?: HTMLElement & { querySelector?: (selector: string) => HTMLElement | null }
}
const labelInputRef = ref<InputRef>()

const label = ref('')
const url = ref('')
const headers = ref<{ key: string; value: string }[]>([])
const configOverrides = ref<{ key: string; value: string }[]>([])
const timeoutSeconds = ref(10)
const tools = ref<McpTool[]>([])

const labelError = ref('')
const urlError = ref('')
const testNotification = ref<{
  type: 'positive' | 'error'
  title?: string
  description: string
} | null>(null)

const emit = defineEmits<{
  confirm: [server: McpServer]
}>()

const resetForm = () => {
  label.value = ''
  url.value = ''
  headers.value = []
  configOverrides.value = []
  timeoutSeconds.value = 10
  tools.value = []
  status.value = 'untested'
  testLoading.value = false
  labelError.value = ''
  urlError.value = ''
  testNotification.value = null
}

const openPanel = (server?: McpServer) => {
  if (server) {
    isEditing.value = true
    label.value = server.label
    url.value = server.url
    headers.value = server.headers.map((h) => ({ ...h }))
    configOverrides.value = Object.entries(server.config_overrides ?? {}).map(([key, value]) => ({
      key,
      value,
    }))
    const timeout = server.timeout_seconds as number | undefined
    timeoutSeconds.value = timeout ?? 10
    tools.value = server.tools.map((t) => ({ ...t }))
    status.value = server.tools.length > 0 ? 'connected' : 'untested'
  } else {
    isEditing.value = false
    resetForm()
  }
  panelOpen.value = true

  void nextTick(() => {
    const inputEl = labelInputRef.value?.$el?.querySelector('input') ?? labelInputRef.value?.$el
    if (inputEl && typeof inputEl.focus === 'function') {
      inputEl.focus()
    }
  })
}

defineExpose({ openPanel })

const validHeaders = computed(() =>
  headers.value.filter((h) => isNotBlank(h.key) && isNotBlank(h.value)),
)

const canTest = computed(() => !testLoading.value)

const showUntestedWarning = computed(
  () => status.value === 'untested' && isNotBlank(label.value) && isNotBlank(url.value),
)

const statusBadgeColor = computed(() =>
  status.value === 'connected' ? 'success' : status.value === 'error' ? 'danger' : 'neutral',
)
const statusLabel = computed(() => t(`tools.mcp.drawer.status.${status.value}`))

const panelTitle = computed(() =>
  isEditing.value ? t('tools.mcp.drawer.editTitle') : t('tools.mcp.drawer.createTitle'),
)

watch(label, (val) => {
  labelError.value = ''
  const safe = toSafeSnakeCase(val)
  if (safe !== val) label.value = safe
})
watch(url, () => {
  status.value = 'untested'
  urlError.value = ''
  testNotification.value = null
  tools.value = []
})
watch(
  headers,
  () => {
    status.value = 'untested'
    testNotification.value = null
    tools.value = []
  },
  { deep: true },
)

const testLoading = ref(false)

const testConnection = async () => {
  labelError.value = isNotBlank(label.value) ? '' : t('tools.mcp.drawer.labelRequired')
  urlError.value = isNotBlank(url.value) ? '' : t('tools.mcp.drawer.urlRequired')
  if (!isNotBlank(label.value) || !isNotBlank(url.value)) return

  testLoading.value = true
  testNotification.value = null
  try {
    const response = await fetchApi<McpTestResult>(
      {
        endpoint: '/api/configurations/mcp-test',
        method: 'POST',
        body: { url: url.value, headers: validHeaders.value },
      },
      { suppressAlert: true },
    )

    if (response.status === 'ok') {
      const discovered: McpDiscoveredTool[] = response.tools ?? []
      tools.value = mergeDiscoveredTools(tools.value, discovered)
      status.value = 'connected'
      testNotification.value = {
        type: 'positive',
        description: t('tools.mcp.testEndpointValid'),
      }
    } else {
      status.value = 'error'
      testNotification.value = {
        type: 'error',
        title: t('tools.mcp.testEndpointNotFound'),
        description: t('tools.mcp.testEndpointNotFoundDesc'),
      }
    }
  } catch {
    status.value = 'error'
    testNotification.value = {
      type: 'error',
      title: t('tools.mcp.testEndpointNotFound'),
      description: t('tools.mcp.testEndpointNotFoundDesc'),
    }
  } finally {
    testLoading.value = false
  }
}

const renderParameters = (tool: McpTool): { name: string; type: string; required: boolean }[] => {
  const schema = tool.inputSchema
  if (!schema || typeof schema !== 'object' || !schema.properties) return []
  const props = schema.properties as Record<string, { type?: string }>
  const required = Array.isArray(schema.required) ? (schema.required as string[]) : []
  return Object.entries(props).map(([name, prop]) => ({
    name,
    type: prop.type ?? 'any',
    required: required.includes(name),
  }))
}

const handleConfirm = () => {
  labelError.value = isNotBlank(label.value) ? '' : t('tools.mcp.drawer.labelRequired')
  urlError.value = isNotBlank(url.value) ? '' : t('tools.mcp.drawer.urlRequired')
  if (!isNotBlank(label.value) || !isNotBlank(url.value)) return
  const server: McpServer = {
    label: label.value.trim(),
    url: url.value.trim(),
    type: 'mcp',
    headers: validHeaders.value,
    config_overrides: Object.fromEntries(
      configOverrides.value.filter((o) => isNotBlank(o.key)).map((o) => [o.key, o.value]),
    ),
    timeout_seconds: timeoutSeconds.value,
    tools: tools.value,
  }
  emit('confirm', server)
  panelOpen.value = false
}

const closePanel = () => {
  panelOpen.value = false
}

const categoryOptions = computed(() => [
  { label: t('tools.mcp.drawer.category.read'), value: 'read' },
  { label: t('tools.mcp.drawer.category.write'), value: 'write' },
])
</script>

<template>
  <ORightPanel teleport v-model:open="panelOpen" :title="panelTitle" size="M">
    <template #header-extra>
      <OBadge :color="statusBadgeColor" :content="statusLabel" variant="secondary" />
    </template>

    <div class="flex flex-col space-y-4">
      <OInput
        ref="labelInputRef"
        v-model="label"
        :label="t('tools.mcp.drawer.label')"
        :placeholder="t('tools.mcp.drawer.labelPlaceholder')"
        :errorMessage="labelError"
        required
        dataTestid="mcp-modal-label"
      />

      <OInput
        v-model="url"
        :label="t('tools.mcp.drawer.url')"
        :placeholder="t('tools.mcp.drawer.urlPlaceholder')"
        :errorMessage="urlError"
        required
        dataTestid="mcp-modal-url"
      />

      <OMultiInput
        v-model="headers"
        :add-label="t('tools.mcp.drawer.addHeader')"
        add-mode="progressive"
        :fields="['key', 'value']"
        :placeholders="[
          t('tools.mcp.drawer.headerKeyPlaceholder'),
          t('tools.mcp.drawer.headerValuePlaceholder'),
        ]"
        :label="t('tools.mcp.drawer.headers')"
        dataTestid="mcp-modal-headers"
      />

      <OMultiInput
        v-if="showMcpConfigOverride"
        v-model="configOverrides"
        :add-label="t('tools.mcp.drawer.addOverride')"
        add-mode="progressive"
        :fields="['key', 'value']"
        :placeholders="[
          t('tools.mcp.drawer.overrideKeyPlaceholder'),
          t('tools.mcp.drawer.overrideValuePlaceholder'),
        ]"
        :label="t('tools.mcp.drawer.configOverrides')"
        dataTestid="mcp-modal-config-overrides"
      />

      <OInput
        v-model.number="timeoutSeconds"
        :label="t('tools.mcp.drawer.timeout')"
        type="number"
        :min="1"
        size="M"
        :widthSize="2"
        dataTestid="mcp-modal-timeout"
      />

      <ONotification
        v-if="showUntestedWarning"
        type="warning"
        :description="t('tools.mcp.drawer.untestedWarning')"
        :closable="false"
        dataTestid="mcp-untested-warning"
      />

      <ONotification
        v-if="testNotification"
        :type="testNotification.type"
        :title="testNotification.title"
        :description="testNotification.description"
        :closable="false"
        dataTestid="mcp-test-notification"
      />

      <div v-if="tools.length" class="flex flex-col gap-2" data-testid="mcp-tools-list">
        <div class="flex items-center justify-between">
          <span class="typography-body-2-strong">{{ t('tools.mcp.drawer.tools') }}</span>
          <span class="typography-caption-1">
            {{ t('tools.mcp.drawer.discovered', { count: tools.length }) }}
          </span>
        </div>

        <div
          v-for="(tool, index) in tools"
          :key="tool.name"
          class="flex items-start gap-3 p-3 border border-neutral-4 rounded-lg"
          :data-testid="`mcp-tool-row-${index}`"
        >
          <OSwitch
            v-model="tool.enabled"
            :dataTestid="`mcp-tool-toggle-${index}`"
            class="w-fit shrink-0"
            :label="tool.enabled ? 'On' : 'Off'"
          />
          <div class="flex flex-col flex-1 min-w-0 gap-1 shrink-0">
            <span class="typography-body-2-strong">{{ tool.name }}</span>
            <span v-if="tool.description" class="typography-body-2">{{ tool.description }}</span>
            <ul v-if="renderParameters(tool).length" class="typography-body-2 mt-1">
              <li v-for="p in renderParameters(tool)" :key="p.name">
                • {{ p.name }}: {{ p.type }}
                <span v-if="p.required" class="italic">
                  ({{ t('tools.mcp.drawer.required') }})
                </span>
              </li>
            </ul>
            <span v-else class="typography-caption-1">
              {{ t('tools.mcp.drawer.noParameters') }}
            </span>
          </div>
          <ODropdown
            v-model="tool.category"
            :options="categoryOptions"
            :disabled="tool.enabled === false"
            size="S"
            :dataTestid="`mcp-tool-category-${index}`"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <OButton
        variant="secondary"
        color="neutral"
        size="L"
        :label="t('tools.mcp.drawer.cancel')"
        @mousedown.prevent
        @click="closePanel"
        dataTestid="mcp-modal-cancel"
      />
      <div class="flex-1" />
      <OButton
        variant="secondary"
        size="L"
        :label="t('tools.mcp.drawer.test')"
        :loading="testLoading"
        :disabled="!canTest"
        @click="testConnection"
        dataTestid="mcp-modal-test"
      />
      <OButton
        size="L"
        color="success"
        :label="t('save')"
        @click="handleConfirm"
        dataTestid="mcp-modal-confirm"
      />
    </template>
  </ORightPanel>
</template>
